import { createReducer, on } from '@ngrx/store';
import { FlightSearchConfig } from '../../flight-search-v2/models/flight-search-config.model';
import { FlightSearchForm } from '../../flight-search-v2/models/flight-search-form.model';
import * as FlightSearchActions from './flight-search.actions';
import { FlightSearchRequest } from '../../flight-search-v2/models/flight-search-request.model';
import { FlightSearchType } from '../../flight-search-v2/enums/flight-search-type.enum';
import { FlightSearchResults } from '../../flight-search-v2/models/flight-search-results.model';
import { FlightData } from '../../flight-search-v2/models/flight-data.model';

export const featureKey = 'flightSearch';

export interface FlightSearchState {
  config: FlightSearchConfig | null;
  flightSearchForm: FlightSearchForm;
  resetSearchForms: boolean;
  searchRequestConfig: FlightSearchRequest | null;
  validateSearchForms: boolean;
  searchType: FlightSearchType;
  flightSearchResults: FlightSearchResults | null;
  flightSearchDisplayData: FlightSearchDisplayData;
  selectedFlights: { flightData: FlightData, cabin: string, isChanging: boolean}[];
  routeHappyApiData: { apiRequests: string[]; apiResponse: string | null }
}

export interface FlightSearchDisplayData {
  searched: boolean;
  loadingResults: boolean;
}

export const initialState: FlightSearchState = {
  config: null,
  flightSearchForm: { outboundSegments: [], returningSegments: [], isValid: false } as FlightSearchForm,
  resetSearchForms: false,
  searchRequestConfig: null,
  validateSearchForms: false,
  searchType: FlightSearchType.ONE_WAY,
  flightSearchDisplayData: {searched: false, loadingResults: false},
  selectedFlights: [],
  flightSearchResults: null,
  routeHappyApiData: { apiRequests: [], apiResponse: null }
};

export const reducer = createReducer(
  initialState,

  on(FlightSearchActions.FlightSearch_ResetSearchForms, (state) => ({
    ...initialState,
    config: state.config,
    resetSearchForms: true,
    validateSearchForms: false,
    searchType: state.searchType,
  })),

  on(FlightSearchActions.FlightSearch_UpdateConfig, (state, action) => ({
    ...state,
    config: {
      ...state.config,
      ...action.config
    } as FlightSearchConfig,
  })),

   on(FlightSearchActions.FlightSearch_SetFlightSearchForm, (state, action) => {
    return { 
      ...state,
      validateSearchForms: false,
      flightSearchForm: {
        outboundSegments: Object.values(action.formData.outboundSegments),
        returningSegments: Object.values(action.formData.returningSegments),
        isValid: action.formData.isValid,
      } as FlightSearchForm,
    };
  }),

  on(FlightSearchActions.FlightSearch_SetFlightSearchFormOutbound, (state, action) => {
    return { 
      ...state,
      validateSearchForms: false,
      flightSearchForm: {
        outboundSegments: Object.values(action.formData.outboundSegments),
        returningSegments: state.flightSearchForm.returningSegments,
        isValid: action.formData.isValid,
      } as FlightSearchForm,
    };
  }),

  on(FlightSearchActions.FlightSearch_ToggleFormReset, (state, action) => {
    return {
      ...state,
      resetSearchForms: action.resetSearchForms,
    };
  }),

  on(FlightSearchActions.FlightSearch_ToggleFormValidation, (state, action) => {
    return {
      ...state,
      validateSearchForms: action.validateSearchForms,
    };
  }),
  
  on(FlightSearchActions.FlightSearch_ResetSearchRequestConfig, (state) => ({
    ...state,
    searchRequestConfig: null,
  })),
  
  on(FlightSearchActions.FlightSearch_SetSearchType, (state, action) => ({
    ...state,
    searchType: action.searchType
  })),

  on(FlightSearchActions.FlightSearch_ResetSearchResults, (state, action) => ({
    ...state,
    flightSearchResults: null,
    flightSearchDisplayData: {...state.flightSearchDisplayData, searched: false, loadingResults: false },
  })),

  on(FlightSearchActions.FlightSearch_StartSearch, (state) => ({
    ...state,
    flightSearchResults: null,
    flightSearchDisplayData: {...state.flightSearchDisplayData, searched: true, loadingResults: true },
    selectedFlights: [],
  })),

  on(FlightSearchActions.FlightSearch_SearchResponse, (state, action) => ({
    ...state,
    flightSearchResults: action.response,
    flightSearchDisplayData: {...state.flightSearchDisplayData, loadingResults: false },
  })),

  on(FlightSearchActions.FlightSearch_AddSelectedFlight, (state, { flightData, cabin }) => {
    const selectedFlights = [...state.selectedFlights];
    let changeFlightIndex = selectedFlights.findIndex(flight => flight.isChanging);
    if(changeFlightIndex === -1) {
      changeFlightIndex = selectedFlights.findIndex(selectedFlight => selectedFlight.flightData.flt_number === flightData.flt_number);
    };
    changeFlightIndex === -1 ? selectedFlights.push({ flightData, cabin, isChanging: false}) : selectedFlights[changeFlightIndex] = { flightData, cabin, isChanging: false };
    return {
      ...state,
      selectedFlights,
    };
  }),

  on(FlightSearchActions.FlightSearch_ChangeSelectedFlight, (state, { flightNumber }) => ({
  ...state,
  selectedFlights: state.selectedFlights.map(selectedFlight => {
      return { ...selectedFlight, isChanging: selectedFlight?.flightData?.flt_number === flightNumber};
    })
  })),

  on(FlightSearchActions.FlightSearch_ClearRemainingSelectedFlights, (state, { changedFlightIndex }) => {
    return {
      ...state,
      selectedFlights: state.selectedFlights
        .slice(0, changedFlightIndex + 1)
        .map(selectedFlight => ({ ...selectedFlight, isChanging: false })),
    };
  }),

  on(FlightSearchActions.FlightSearch_SetRouteHappyApiData, (state, action) => ({
    ...state,
    routeHappyApiData: {
      apiRequests: action.apiRequests,
      apiResponse: action.apiResponse
    }
  })),
);
